<template>
  <div class="messaging-inbox-page" data-testid="messaging-inbox-page">

    <!-- No devices fallback -->
    <div v-show="noDevicesFound" class="no-devices-container">
      <div class="no-devices-content">
        <i class="pi pi-comments no-devices-icon"></i>
        <h3>{{ t('Nenhum dispositivo WhatsApp encontrado') }}</h3>
        <p>{{ t('Não foram encontrados dispositivos WhatsApp configurados e conectados para as franquias selecionadas.') }}</p>
        <p class="no-devices-hint">{{ t('Acesse a tela de WhatsApp Franquias para cadastrar e conectar um dispositivo.') }}</p>
      </div>
    </div>

    <!-- Inbox + bottom bar (always mounted so inboxContainer ref is never lost) -->
    <div v-show="!noDevicesFound" class="inbox-and-bar">
      <div class="inbox-wrapper" :style="{ height: inboxHeight }">
        <div ref="inboxContainer" style="height: 100%; width: 100%;" />
      </div>
      <MessagingBottomBar @height-change="onBarHeightChange" />
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { mount, unmount } from '@ivanamato/messaging-inbox'
import '@ivanamato/messaging-inbox/style.css'
import { eventBus } from '../../eventBus'
import { useMessagingInbox } from '../../composables/useMessagingInbox'
import { useMessagingChatCliente } from '../../composables/useMessagingChatCliente'
import { useDevicesFranquias } from '../../composables/useDevicesFranquias'
import { storageService } from '../../services/storageService'
import { log, warn, error as logError } from '../../utils/DebugLogger'
import MessagingBottomBar from './MessagingBottomBar.vue'

const P = 'MessagingInboxPage'

type DeviceId = number | string

interface InboxDevice {
  id: DeviceId
  instanceName: string
  [key: string]: unknown
}

interface InboxInstance {
  openChat: (phone: string, opts?: { deviceId?: DeviceId }) => void
  sendMessage: (text: string) => void
  setWebSocketEnabled: (deviceId: DeviceId, enabled: boolean) => void
}

interface PendingChatData {
  phone: string
  deviceId?: DeviceId
  prefillMessage?: string
}

interface SelectConversationPayload {
  phone: string
  deviceId?: DeviceId | null
}

const TOPBAR_HEIGHT = 60
const WS_RECONNECT_MAX_RETRIES = 3
const WS_RECONNECT_DELAY_MS = 4000

const { t } = useI18n()
const inboxContainer = ref<HTMLElement | null>(null)
const inboxInstance = ref<InboxInstance | null>(null)
const noDevicesFound = ref<boolean>(false)
const barHeight = ref<number>(40)

const inboxHeight = computed(() => `calc(100vh - ${TOPBAR_HEIGHT}px - ${barHeight.value}px)`)

const onBarHeightChange = (h: number): void => {
  barHeight.value = h
}
const { reconnectDevice } = useDevicesFranquias()
const { devices, fetchInboxDevices } = useMessagingInbox()
const { resolveChatActions, resolveChatTags, resolveChatTagsBulk } = useMessagingChatCliente()

const translations = {
  'conversationList.chats': 'Conversas',
  'conversationList.autoUpdating': 'Atualizando automaticamente...',
  'conversationList.searchPlaceholder': 'Buscar conversa...',
  'conversationList.noConversationsFound': 'Nenhuma conversa encontrada',
  'conversationList.noConversationsYet': 'Nenhuma conversa ainda',
  'conversationList.selectInstance': 'Selecione um dispositivo para ver as conversas',
  'conversationList.yesterday': 'Ontem',
  'messageView.title': 'Mensagens',
  'messageView.emptyStateDescription': 'Selecione uma conversa para ver as mensagens',
  'messageView.encryptionNotice': 'As mensagens são criptografadas de ponta a ponta',
  'messageView.messageDeleted': 'Mensagem apagada',
  'messageView.downloadFile': 'Baixar arquivo',
  'messageView.notDelivered': 'Não entregue',
  'messageView.readOnlyMode': 'Modo somente leitura',
  'messageView.uploadFile': 'Enviar arquivo',
  'messageView.sendInteractiveMessage': 'Enviar mensagem interativa',
  'messageView.typeMessage': 'Digite uma mensagem...',
  'messageView.sendTemplate': 'Enviar template',
  'messageView.noInboundMessages': 'Sem mensagens recebidas',
  'messageView.outside24HourWindow': 'Fora da janela de 24 horas',
  'messageView.today': 'Hoje',
  'messageView.yesterday': 'Ontem',
  'instanceSelector.loadingDevices': 'Carregando dispositivos...',
  'instanceSelector.noDevicesConfigured': 'Nenhum dispositivo configurado',
  'instanceSelector.selectDevice': 'Selecionar dispositivo',
  'instanceSelector.allDevices': 'Todos os dispositivos',
  'instanceSelector.connected': 'Conectado',
  'instanceSelector.close': 'Desconectado',
  'instanceSelector.mergeDevices': 'Unificar dispositivos',
  'contextMenu.messageOptions': 'Opções da mensagem',
  'contextMenu.copy': 'Copiar',
  'contextMenu.forward': 'Encaminhar',
  'contextMenu.delete': 'Apagar',
  'contextMenu.deleteMessage': 'Apagar mensagem',
  'contextMenu.deleteConfirmation': 'Tem certeza que deseja apagar esta mensagem?',
  'contextMenu.cancel': 'Cancelar',
  'contextMenu.deleteForEveryone': 'Apagar para todos',
  'forwardDialog.title': 'Encaminhar mensagem',
  'forwardDialog.description': 'Selecione um contato para encaminhar a mensagem',
  'forwardDialog.forwarding': 'Encaminhando...',
  'forwardDialog.searchContacts': 'Buscar contatos...',
  'forwardDialog.noContactsFound': 'Nenhum contato encontrado',
  'forwardDialog.cancel': 'Cancelar',
  'forwardDialog.forward': 'Encaminhar',
  'forwardDialog.genericError': 'Erro ao encaminhar mensagem',
  'templateSelector.title': 'Selecionar template',
  'templateSelector.description': 'Escolha um template para enviar',
  'templateSelector.noTemplates': 'Nenhum template disponível',
  'templateSelector.cancel': 'Cancelar',
  'templateSelector.send': 'Enviar',
  'templateSelector.notSupported': 'Templates não suportados',
  'templateParameters.title': 'Parâmetros do template',
  'templateParameters.description': 'Preencha os parâmetros do template',
  'templateParameters.fillAllParameters': 'Preencha todos os parâmetros',
  'templateParameters.example': 'Exemplo',
  'templateParameters.back': 'Voltar',
  'templateParameters.sendTemplate': 'Enviar template',
  'templateParameters.notSupported': 'Parâmetros não suportados',
  'interactiveDialog.title': 'Mensagem interativa',
  'interactiveDialog.description': 'Envie uma mensagem com botões',
  'interactiveDialog.headerLabel': 'Cabeçalho',
  'interactiveDialog.headerPlaceholder': 'Título da mensagem',
  'interactiveDialog.bodyLabel': 'Corpo',
  'interactiveDialog.bodyPlaceholder': 'Texto da mensagem',
  'interactiveDialog.buttonsLabel': 'Botões',
  'interactiveDialog.addButton': 'Adicionar botão',
  'interactiveDialog.buttonPlaceholder': 'Texto do botão',
  'interactiveDialog.moreButtons': 'mais botões',
  'interactiveDialog.cancel': 'Cancelar',
  'interactiveDialog.send': 'Enviar',
  'interactiveDialog.validationError': 'Preencha todos os campos obrigatórios',
  'interactiveDialog.noConversation': 'Selecione uma conversa primeiro',
  'interactiveDialog.noInstance': 'Selecione um dispositivo primeiro',
  'interactiveDialog.sendError': 'Erro ao enviar mensagem',
  'mediaMessage.unavailable': 'Mídia indisponível',
  'mediaMessage.downloadDocument': 'Baixar documento',
}

// Module-level reference — immune to Vue ref lifecycle issues.
// The Vue ref (inboxInstance) can lose its value when template re-renders
// due to filter.updated race conditions. This always holds the latest instance.
let _currentInbox: InboxInstance | null = null

// Track WebSocket reconnect attempts per device to avoid infinite loops
const wsReconnectAttempts = new Map<DeviceId, number>()

const handleWebSocketError = async (deviceId: DeviceId, error: string | undefined): Promise<void> => {
  warn(P, 'handleWebSocketError', `Error on device ${deviceId}:`, error)

  if (!error || !error.includes('Invalid namespace')) return

  const attempts = wsReconnectAttempts.get(deviceId) || 0
  if (attempts >= WS_RECONNECT_MAX_RETRIES) {
    logError(P, 'handleWebSocketError', `Device ${deviceId}: max reconnect attempts (${WS_RECONNECT_MAX_RETRIES}) reached, giving up`)
    return
  }

  wsReconnectAttempts.set(deviceId, attempts + 1)
  log(P, 'handleWebSocketError', `Device ${deviceId}: "Invalid namespace" detected, attempt ${attempts + 1}/${WS_RECONNECT_MAX_RETRIES} — re-triggering instance connection...`)

  // Find the instanceName for this deviceId
  const device = (devices.value as InboxDevice[]).find((d) => d.id === deviceId)
  if (!device) {
    logError(P, 'handleWebSocketError', `Device ${deviceId} not found in devices list`)
    return
  }

  try {
    // Disable WebSocket first so the library stops retrying
    const inst = _currentInbox || inboxInstance.value
    if (inst) inst.setWebSocketEnabled(deviceId, false)

    // Call backend to re-trigger Evolution API instance connection
    await reconnectDevice(device.instanceName)

    log(P, 'handleWebSocketError', `Device ${deviceId}: reconnect API called, waiting ${WS_RECONNECT_DELAY_MS}ms for namespace registration...`)

    // Wait for Evolution API to register the namespace
    await new Promise<void>((resolve) => setTimeout(resolve, WS_RECONNECT_DELAY_MS))

    // Re-enable WebSocket to trigger a fresh connection
    if (inst) inst.setWebSocketEnabled(deviceId, true)

    log(P, 'handleWebSocketError', `Device ${deviceId}: WebSocket re-enabled`)
  } catch (err) {
    logError(P, 'handleWebSocketError', `Device ${deviceId}: reconnect failed:`, err)
  }
}

const mountInbox = async (): Promise<void> => {
  await fetchInboxDevices()
  await nextTick()

  if (devices.value.length === 0) {
    noDevicesFound.value = true
    return
  }

  noDevicesFound.value = false
  await nextTick()
  await nextTick() // second tick ensures ref is assigned after v-else re-render

  const container = inboxContainer.value
  if (container) {
    unmount(container)
    wsReconnectAttempts.clear()

    const instance = mount(container, {
      devices: devices.value,
      translations,
      chatActions: resolveChatActions,
      chatTags: resolveChatTags,
      chatTagsBulk: resolveChatTagsBulk,
      debug: true,
      onWebSocketError: handleWebSocketError,
    }) as unknown as InboxInstance
    inboxInstance.value = instance
    _currentInbox = instance
  }
}

const handleFilterUpdated = (): void => {
  mountInbox()
}

const handleSelectConversation = (payload?: SelectConversationPayload): void => {
  if (!payload) return
  const { phone, deviceId } = payload
  const inst = _currentInbox || inboxInstance.value
  inst?.openChat(phone, { deviceId: deviceId ?? undefined })
}

onMounted(() => {
  log(P, 'onMounted', 'Iniciando')
  mountInbox()
  eventBus.on('filter.updated', handleFilterUpdated)
  eventBus.on('filter.applied', handleFilterUpdated)
  eventBus.on('whatsapp.select-conversation', handleSelectConversation)

  // Verificar se há chat pendente no sessionStorage (redirecionamento de aniversariantes)
  setTimeout(() => {
    const chatData = storageService.session.getJSON('whatsapp_pending_chat') as PendingChatData | null
    if (chatData) {
      log(P, 'onMounted', 'Chat pendente encontrado:', chatData)
      try {
        // Abrir o chat diretamente usando o método openChat da instância
        if (inboxInstance.value && chatData.phone) {
          log(P, 'onMounted', 'Abrindo chat para:', chatData.phone)
          log(P, 'onMounted', 'Instância:', inboxInstance.value)

          // Usar openChat com o número de telefone
          inboxInstance.value.openChat(chatData.phone, {
            deviceId: chatData.deviceId ?? undefined
          })

          // Se há mensagem preenchida, enviar após abrir o chat
          if (chatData.prefillMessage) {
            setTimeout(() => {
              log(P, 'onMounted', 'Enviando mensagem preenchida:', chatData.prefillMessage)
              inboxInstance.value?.sendMessage(chatData.prefillMessage as string)
            }, 1000)
          }

          // Limpar o sessionStorage após usar
          storageService.session.remove('whatsapp_pending_chat')
          log(P, 'onMounted', 'SessionStorage limpo')
        } else {
          warn(P, 'onMounted', 'Instância não disponível ou phone vazio')
        }
      } catch (err) {
        logError(P, 'onMounted', 'Erro ao processar chat pendente:', err)
      }
    } else {
      log(P, 'onMounted', 'Nenhum chat pendente no sessionStorage')
    }
  }, 1000)
})

onBeforeUnmount(() => {
  if (inboxContainer.value) {
    unmount(inboxContainer.value)
  }
  _currentInbox = null
  eventBus.off('filter.updated', handleFilterUpdated)
  eventBus.off('filter.applied', handleFilterUpdated)
  eventBus.off('whatsapp.select-conversation', handleSelectConversation)
})
</script>

<style scoped>
.messaging-inbox-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.inbox-and-bar {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.inbox-wrapper {
  overflow: hidden;
  flex-shrink: 0;
}

.no-devices-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.no-devices-content {
  text-align: center;
  max-width: 480px;
  padding: 40px;
}

.no-devices-icon {
  font-size: 48px;
  color: #9ca3af;
  margin-bottom: 16px;
}

.no-devices-content h3 {
  margin: 0 0 12px 0;
  color: #374151;
  font-size: 20px;
}

.no-devices-content p {
  margin: 0 0 8px 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.no-devices-hint {
  color: #9ca3af !important;
  font-style: italic;
}
</style>
